/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { forOwn, uniqueId } from 'lodash';

import requireAMD, { mixinComponentDidLoadModules } from '../utils/requireAMD';

import './AceEditor.scss';

// This will import a ace object,and ace needs to be no-conflict version.
@requireAMD({ ace: 'ace/noconflict/ace' })
@mixinComponentDidLoadModules
export default class AceEditor extends Component<any, any> {
	static propTypes = {
		mode: PropTypes.string,
		focus: PropTypes.bool,
		activeTheme: PropTypes.string,
		theme: PropTypes.string,
		highContrastTheme: PropTypes.string,
		value: PropTypes.string,
		commands: PropTypes.array,
		setOptions: PropTypes.object,
		aceConfig: PropTypes.object,
		editorProps: PropTypes.object,
		onChange: PropTypes.func,
		onCursorChange: PropTypes.func,
		onTextSelectionChange: PropTypes.func,
		width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		getAceEditorRef: PropTypes.func,
		label: PropTypes.string,
	};

	static defaultProps = {
		width: '100%',
		height: '100%',
	};

	componentWillReceiveProps(nextProps) {
		const oldProps = this.props;

		if (nextProps.setOptions !== oldProps.setOptions) {
			this._setOptions(nextProps.setOptions);
		}
	}

	componentWillUnmount() {
		if (this._editor) {
			this._editor.destroy();
			this._editor = null;
		}
	}

	componentDidLoadModules() {
		const {
			mode,
			activeTheme,
			theme,
			highContrastTheme,
			commands,
			focus,
			editorProps,
			aceConfig,
			value,
			onChange,
			onCursorChange,
			onTextSelectionChange,
			setOptions,
		} = this.props;

		const { ace } = window as any;

		this._editor = ace.edit(this._id);

		forOwn(editorProps, (val, key) => {
			this._editor[key] = val;
		});

		forOwn(aceConfig, (val, key) => {
			ace.config.set(key, val);
		});

		if (mode) {
			this._editor.getSession().setMode(`ace/mode/${mode}`);
		}

		if (activeTheme) {
			this._editor.setTheme(`ace/theme/${activeTheme}`);
		}
		if (highContrastTheme) {
			this._editor.highContrastThemeOn = () =>
				this._editor.setTheme(`ace/theme/${highContrastTheme}`);
			this._editor.highContrastThemeOff = () => this._editor.setTheme(`ace/theme/${theme}`);
		}

		this._editor.completers = []; // allow custom completers only
		this._editor.setValue(value != null ? value : '', -1);
		this._editor.on('change', () => onChange(this._editor.getValue()));
		this._editor.on('changeSelection', () =>
			onTextSelectionChange(
				this._editor.session.getTextRange(this._editor.getSelectionRange())
			)
		);
		const selection = this._editor.session.getSelection();

		selection
			.on('changeCursor', () => {
				const position = this._editor.getCursorPosition();
				const token = this._editor.session.getTokenAt(position.row, position.column);

				token && onCursorChange(token);
			})
			.bind(this);

		this._setOptions(setOptions);

		if (commands) {
			commands.forEach(command => {
				this._editor.commands.addCommand(command);
			});
		}

		// allow the browser to handle the tab key by changing the element
		this._editor.commands.bindKey('Tab', null);
		this._editor.commands.bindKey('Shift-Tab', null);

		this.props.getAceEditorRef && this.props.getAceEditorRef(this._editor);

		if (focus) {
			this.focus();
		}
	}

	focus() {
		this._editor && this._editor.focus();
	}

	_editor: any;
	_node: HTMLElement;

	_id = uniqueId('__aceEditor_');

	_ref = node => {
		this._node = node;
	};

	_setOptions(options) {
		if (this._editor) {
			options &&
				forOwn(options, (val, key) => {
					this._editor.setOption(key, val);
				});

			const textarea = this._node.querySelector('textarea');

			if (textarea) {
				if (options) {
					textarea.readOnly = !!options.readOnly;
					textarea.setAttribute('aria-readonly', String(!!options.readOnly));
				}
				textarea.setAttribute('aria-label', this.props.label || '');
			}
		}
	}

	render() {
		const { width, height } = this.props;

		return <div id={this._id} ref={this._ref} style={{ width, height }} />;
	}
}
